/**
 * The seam between "pick a point on a map" and whichever map draws it.
 *
 * OpenStreetMap today, Google later — that swap was a stated requirement
 * before a line of this was written, so the map library is confined to one
 * file behind this interface, the same way a payment gateway is. Nothing above
 * this line imports Leaflet, and a Google implementation is a second file plus
 * one line in the registry.
 *
 * The interface is IMPERATIVE rather than a React component on purpose. Map
 * libraries own a DOM node and a lot of mutable state; wrapping that in a
 * component per vendor means re-deriving the same lifecycle bugs for each one.
 * A handle with `setCenter` and `destroy` is the smallest thing both vendors
 * can honestly implement.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export interface MapPickerOptions {
  /** Where to open. The pin starts here. */
  center: LatLng;
  zoom: number;
  /** Fires as the customer moves the map. The pin is fixed to the centre. */
  onCenterChanged: (center: LatLng) => void;
}

/*
 * THE CONTAINER'S ACCESSIBLE NAME IS NOT A PROVIDER'S JOB, and that is a
 * decision paid for in a real bug. Both providers used to set `role` and
 * `aria-label` on the node they were handed, and the Google one removed them
 * again on teardown.
 *
 * Creating a map is asynchronous, so a component that mounts, unmounts and
 * remounts — which React does to every component in StrictMode, and which a
 * customer does by flipping between delivery and collection — has two
 * `create` calls in flight over ONE container. The first one to finish is
 * already stale; when its `destroy` lands it strips the attributes the live
 * map had just set, and the map is left with no accessible name at all. The
 * OSM provider only escaped it by never cleaning up after itself.
 *
 * So the component declares the role and the name in its own JSX, where
 * React owns them and no late teardown can reach them.
 */

export interface MapPickerHandle {
  /**
   * Move the map WITHOUT reporting it as the customer's answer.
   *
   * Only a move the customer made counts, so a programmatic recentre — onto
   * the PIN code's area, or a device fix — must not come back through
   * `onCenterChanged`. Whoever calls this knows what the move means and says
   * so itself.
   */
  setCenter: (center: LatLng, zoom?: number) => void;
  /** Release the DOM node and every listener. Must be safe to call twice. */
  destroy: () => void;
}

export interface MapProvider {
  readonly name: string;
  /**
   * Draw a picker into `container`.
   *
   * Async because a vendor's script and stylesheet may still be loading; the
   * caller shows its own placeholder until this resolves.
   */
  create: (container: HTMLElement, options: MapPickerOptions) => Promise<MapPickerHandle>;
}

/**
 * Where the map opens when we have nothing better.
 *
 * The SHOP's own doorstep — Kalamassery, Kochi. A customer who declines the
 * location prompt starts looking at the area we actually deliver to, not at
 * the middle of the country. Overridden the moment a device fix, a search
 * result or a saved pin arrives.
 *
 * This said Kozhikode until the shop's real coordinates were known, which put
 * every such customer 180km from anywhere we serve.
 */
export const DEFAULT_CENTER: LatLng = { lat: 10.046729, lng: 76.321475 };

/** Close enough to see individual buildings, which is what a doorstep needs. */
export const DEFAULT_ZOOM = 16;
/** What we jump to once a real fix arrives. */
export const LOCATED_ZOOM = 18;

/** Six decimals is ~11cm. More is noise, and it bloats every payload. */
export function roundCoordinate(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
